import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/app/AppShell";
import VideoRoomLiveBanner from "@/components/app/VideoRoomLiveBanner";
import { api, STATUSES, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Users, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ChangePasswordModal from "@/components/app/ChangePasswordModal";
import BulkImportDialog from "@/components/app/BulkImportDialog";
import RecentSMSInbox from "@/components/app/RecentSMSInbox";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLORS = {
  "Getting Started": "bg-sky-100 text-sky-700",
  "Credit Analysis": "bg-blue-100 text-blue-700",
  "Disputes Submitted": "bg-purple-100 text-purple-700",
  "Bureau Processing": "bg-indigo-100 text-indigo-700",
  "Results Coming In": "bg-cyan-100 text-cyan-700",
  "Credit Optimization": "bg-fuchsia-100 text-fuchsia-700",
  "Funding Ready": "bg-amber-100 text-amber-800",
  "Funding in Progress": "bg-teal-100 text-teal-700",
  "Completed": "bg-green-100 text-green-700",
  "Completed After 30 Days": "bg-emerald-100 text-emerald-700",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [mine, setMine] = useState(false);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      if (mine) params.mine = true;
      const { data } = await api.get("/admin/clients", { params });
      setClients(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/admin/team-members").then(({ data }) => setTeam(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, mine]);

  const stats = useMemo(() => {
    const total = clients.length;
    const active = clients.filter((c) => c.effective_status !== "Completed" && c.effective_status !== "Completed After 30 Days").length;
    const done = total - active;
    const issues = clients.filter((c) => c.effective_status === "Credit Analysis").length;
    return { total, active, done, issues };
  }, [clients]);

  return (
    <AppShell>
      <ChangePasswordModal forced />
      <VideoRoomLiveBanner />

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 cq-in">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Admin Console</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900">Clients</h1>
          <p className="text-slate-500 mt-1">Manage every client's credit repair journey.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white h-11 rounded-xl px-5" data-testid="add-client-btn">
              <Plus size={18} className="mr-1" /> Add client
            </Button>
          </DialogTrigger>
          <AddClientDialog team={team} onCreated={() => { setOpenAdd(false); load(); }} />
        </Dialog>
        <BulkImportDialog onImported={load} />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Total clients" value={stats.total} color="text-slate-700" />
        <StatCard icon={Clock} label="In progress" value={stats.active} color="text-amber-600" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats.done} color="text-green-600" />
        <StatCard icon={AlertTriangle} label="In analysis" value={stats.issues} color="text-blue-600" />
      </div>

      {/* Recent SMS Replies */}
      <RecentSMSInbox />

      <Card className="p-4 sm:p-6 rounded-2xl border-slate-100">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Search by name, email, phone…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-10 h-11 rounded-xl bg-slate-50 border-slate-200"
              data-testid="search-input"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full sm:w-64 h-11 rounded-xl bg-slate-50 border-slate-200" data-testid="status-filter">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`filter-${s.toLowerCase().replace(/ /g, "-")}`}>{s}</SelectItem>)}
              <SelectItem value="Completed After 30 Days">Completed After 30 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="h-11 rounded-xl" data-testid="search-btn">Search</Button>
        </div>

        {user?.role === "team_member" && (
          <label className="flex items-center gap-2 mb-4 text-sm font-semibold text-slate-700">
            <Switch checked={mine} onCheckedChange={setMine} data-testid="assigned-to-me-toggle" />
            Only show clients assigned to me
          </label>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>Est. completion</TableHead>
                <TableHead>Assigned</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Loading…</TableCell></TableRow>}
              {!loading && clients.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8" data-testid="no-clients">No clients yet. Add one to get started.</TableCell></TableRow>
              )}
              {!loading && clients.map((c) => (
                <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/admin/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <TableCell>
                    <div className="font-semibold text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={c.effective_status}
                      onValueChange={async (v) => {
                        try {
                          await api.put(`/admin/clients/${c.id}`, { status: v, manual_status_override: true });
                          toast.success(`${c.name} → ${v}`);
                          load();
                        } catch (err) {
                          toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
                        }
                      }}
                    >
                      <SelectTrigger
                        className={`h-8 rounded-full text-xs font-semibold border-0 ${STATUS_COLORS[c.effective_status] || "bg-slate-100 text-slate-700"} hover:opacity-80`}
                        data-testid={`row-status-${c.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`row-status-opt-${c.id}-${s.toLowerCase().replace(/ /g, "-")}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(c.start_date)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(c.estimated_completion)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{c.assigned_team_member_name || <span className="text-slate-400">Unassigned</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </AppShell>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">{label}</p>
          <p className={`font-display text-3xl font-black mt-1 ${color}`} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>{value}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-2 text-slate-500"><Icon size={18} /></div>
      </div>
    </Card>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

function AddClientDialog({ team, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", start_date: new Date().toISOString().slice(0, 10),
    status: "Getting Started", assigned_team_member_id: "", create_login: true, login_password: "ClientPass123!",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_date: new Date(form.start_date).toISOString(),
        assigned_team_member_id: form.assigned_team_member_id || null,
      };
      await api.post("/admin/clients", payload);
      toast.success("Client added");
      onCreated();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to add client");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg" data-testid="add-client-dialog">
      <DialogHeader><DialogTitle className="font-display text-2xl">Add a client</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="new-client-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="new-client-email" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-client-phone" /></div>
          <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="new-client-start" /></div>
          <div className="col-span-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="new-client-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Assign team member</Label>
            <Select value={form.assigned_team_member_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_team_member_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="new-client-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 rounded-xl bg-slate-50 p-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.create_login} onChange={(e) => setForm({ ...form, create_login: e.target.checked })} data-testid="new-client-create-login" />
              Create client login account
            </label>
            {form.create_login && (
              <div className="mt-2">
                <Label>Temporary password</Label>
                <Input value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} data-testid="new-client-login-password" />
                <p className="text-xs text-slate-500 mt-1">The client will be forced to change this at first login.</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="new-client-submit">
            {busy ? "Adding…" : "Add client"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
